import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { LogOut, Eye } from '../lib/icons';
import { endImpersonation, getImpersonationState } from '../lib/impersonation';

const ImpersonationBanner = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const impersonationState = getImpersonationState();

  if (!impersonationState) {
    return null;
  }

  const handleExitImpersonation = async () => {
    try {
      setLoading(true);
      setError(null);
      await endImpersonation();
    } catch (err: any) {
      console.error('Error exiting impersonation:', err);
      setError(err.message || 'Failed to exit impersonation');
      setLoading(false);
    }
  };

  return (
    <View style={styles.banner}>
      <View style={styles.content}>
        <View style={styles.messageContainer}>
          <Eye size={20} color="#92400e" />
          <Text style={styles.message}>
            Viewing as <Text style={styles.userName}>{impersonationState.impersonatedUserName}</Text>
            {' • '}
            <Text style={styles.subtext}>You are in impersonation mode</Text>
          </Text>
        </View>
        
        <Pressable
          onPress={handleExitImpersonation}
          disabled={loading}
          style={({ pressed }) => [
            styles.exitButton,
            (loading || pressed) && styles.exitButtonPressed
          ]}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#ffffff" />
          ) : (
            <>
              <LogOut size={16} color="#ffffff" />
              <Text style={styles.exitButtonText}>Exit Impersonation</Text>
            </>
          )}
        </Pressable>
      </View>
      
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#fef3c7',
    borderBottomWidth: 2,
    borderBottomColor: '#f59e0b',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
    }),
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 16,
    ...(Platform.OS === 'web' && {
      flexWrap: 'wrap' as any,
    }),
  },
  messageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    flexShrink: 1,
  },
  message: {
    fontSize: 14,
    color: '#92400e',
    fontWeight: '500',
    flexShrink: 1,
    ...(Platform.OS === 'web' && {
      flexWrap: 'wrap' as any,
    }),
  },
  userName: {
    fontWeight: '700',
    color: '#78350f',
  },
  subtext: {
    fontWeight: '400',
    color: '#92400e',
  },
  exitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#dc2626',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  exitButtonPressed: {
    opacity: 0.8,
  },
  exitButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  errorContainer: {
    backgroundColor: '#fecaca',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#f87171',
  },
  errorText: {
    color: '#991b1b',
    fontSize: 12,
  },
});

export default ImpersonationBanner;

